/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
    ContentInfoCardKernel,
    InfoCardKernel,
    InfoCardKernelType,
    OfferInfoCardKernel,
    VodOfferInfoCardKernel,
} from 'client-code/actions';
import { EpisodeInfo } from 'client-code/episode-guide';
import { UpcomingOffer } from 'client-code/upcoming-offers';
import { OfferVodItemKernel, VodItem } from 'client-code/vod-browse';

import { APPLICATION_FEATURE_AREA } from '../../config';
import {
    IInfoCard,
    IInfoCardProperty,
    InfoCardManager,
    InfoCardUserInputHandler,
} from '../infocard';
import { NdvrInteractor, ServicesClientImpl, VodInteractor } from '../interactors';
import { Log } from '../logging';
import { TListViewType } from '../types';

export class ListViewInfoCardUserInputHandler extends InfoCardUserInputHandler {
    private mInfoCard: IInfoCard = InfoCardManager.getInstance();

    // eslint-disable-next-line class-methods-use-this
    private async getItem(
        listType: TListViewType,
        id: string,
        offset: number,
        partnerId?: string,
        collapsedVodItemId?: string,
    ): Promise<(VodItem | UpcomingOffer | EpisodeInfo)[] | null> {
        let res: (VodItem | UpcomingOffer | EpisodeInfo)[] | null = null;
        if (listType === 'upcoming') {
            const ndvrInteractor = new NdvrInteractor();
            res = (
                await ndvrInteractor.getUpcomingOffersForCollectionId({
                    objectId: id,
                    minEndTime: new Date().toISOString(),
                    offset,
                    limit: 1,
                    screenType: APPLICATION_FEATURE_AREA.LIST_VIEW_SCREEN,
                })
            ).upcomingOffers;
        } else if (
            listType === 'myRentals' ||
            listType === 'myadultRentals' ||
            listType === 'textCurrentRentals'
        ) {
            const vodInteractor = new VodInteractor();
            res =
                (
                    await vodInteractor.getVodBrowseData({
                        parentVodItemId: id,
                        offset,
                        limit: 1,
                        includeAdult: new ServicesClientImpl().isPCHideAdultContentEnabled(),
                    })
                ).vodItemList?.vodItemList || [];
        } else if (listType === 'allEpisodes' || listType === 'onDemand') {
            const ndvrInteractor = new NdvrInteractor();
            const getEpisodesParams: any = {
                unifiedCollectionId: id,
                filter: listType === 'onDemand' ? 'vodOnDemand' : 'all',
                offset,
                limit: 1,
            };

            if (listType === 'onDemand') {
                getEpisodesParams.partnerId = partnerId || '';
                getEpisodesParams.collapsedVodItemId = collapsedVodItemId || '';
            } else {
                if (partnerId) getEpisodesParams.partnerId = partnerId;
                if (collapsedVodItemId) getEpisodesParams.collapsedVodItemId = collapsedVodItemId;
            }

            res = (await ndvrInteractor.getEpisodes(getEpisodesParams)).episodeInfo;
        }

        return res;
    }

    private async horizontalKeyNavigationHelper(direction: 'left' | 'right'): Promise<void> {
        const {
            activeItemIndex,
            initialPageOffset,
            totalItemCount,
            initialPageList,
            listType,
            maxInPage,
            id,
            partnerId,
            collapsedVodItemId,
        } = this.mInfoCard.getContextData(ListViewInfoCardUserInputHandler.name) || {};

        const nextItemIndex = (direction === 'left' ? -1 : 1) + activeItemIndex;
        if (nextItemIndex < 0 || nextItemIndex >= totalItemCount) return;

        let selectedItem;
        const isItemInInitialPage =
            nextItemIndex >= initialPageOffset &&
            nextItemIndex <= initialPageOffset + maxInPage - 1;
        if (isItemInInitialPage) selectedItem = initialPageList[nextItemIndex - initialPageOffset];
        else {
            try {
                const response = await this.getItem(
                    listType,
                    id,
                    nextItemIndex,
                    partnerId,
                    collapsedVodItemId,
                );
                [selectedItem] = response || [];
            } catch (error) {
                Log.e(
                    'ListViewInfoCardUserInputHandler',
                    'Error fetching item for navigation',
                    error,
                );
                this.mInfoCard.dismiss();
                return;
            }
        }

        const tActiveItemIndex = nextItemIndex;
        this.mInfoCard.hideActionButtons();
        this.mInfoCard.setProperties({
            hasItemsBefore: tActiveItemIndex > 0,
            hasItemsAfter: tActiveItemIndex < totalItemCount - 1,
        });
        this.mInfoCard.setContextData(ListViewInfoCardUserInputHandler.name, {
            activeItemIndex: tActiveItemIndex,
            initialPageOffset,
            totalItemCount,
            initialPageList,
            listType,
            maxInPage,
            id,
            partnerId,
            collapsedVodItemId,
        });
        this.mInfoCard.setInfoCardKernel(
            this.getInfoCardKernel(selectedItem, listType, {
                partnerId,
                collapsedVodItemId,
            }),
        );
    }

    /**
     * @override
     */
    public onKeyLeft(properties: IInfoCardProperty): void {
        if (!properties.hasItemsBefore) return;
        this.horizontalKeyNavigationHelper('left');
    }

    /**
     * @override
     */
    public onKeyRight(properties: IInfoCardProperty): void {
        if (!properties.hasItemsAfter) return;
        this.horizontalKeyNavigationHelper('right');
    }

    // eslint-disable-next-line class-methods-use-this
    public getInfoCardKernel(
        item: EpisodeInfo | UpcomingOffer | VodItem,
        listType: TListViewType,
        vodContext?: {
            partnerId?: string;
            collapsedVodItemId?: string;
        },
    ): InfoCardKernel {
        if (listType === 'upcoming')
            return {
                infoCardKernelType: InfoCardKernelType.OfferInfoCardKernel,
                offerId: (item as UpcomingOffer).offerId,
            } as OfferInfoCardKernel;

        if (listType === 'allEpisodes' || listType === 'onDemand') {
            return {
                infoCardKernelType: InfoCardKernelType.ContentInfoCardKernel,
                contentId: (item as EpisodeInfo).contentId,
                partnerId: vodContext?.partnerId,
                collapsedVodItemId: vodContext?.collapsedVodItemId,
            } as ContentInfoCardKernel;
        }

        return {
            infoCardKernelType: InfoCardKernelType.VodOfferInfoCardKernel,
            offerId: ((item as VodItem).vodItemKernel as OfferVodItemKernel).offerId,
        } as VodOfferInfoCardKernel;
    }
}
